import Dataman from 'App/Dataman';
import { sortAsc, sortBy } from 'App/utils/Helpers';
import {
  FILE_GROUP_MODULE_PRODUCTS,
  USP_TYPE_ID,
  CAT_TYPES,
  CAT_PRODUCTS,
  CAT_CONSUMABLE_VARIATIONS,
} from 'App/utils/Collections';
import { loadFileGroups } from './file';
import ItemMaster from 'App/ItemMaster';
import { TYPE_ID_IMAGES } from 'Lib/enums/HardcodedIds';

const COMPETITORS_TYPE_ID = 279;
const CANNA_COMPETITORS_TYPE_ID = 350;

export function loadConsumableVariations() {
  return Dataman.DB[CAT_CONSUMABLE_VARIATIONS].getList({
    properties: ['id', 'name', 'description'],
    itemsPerPage: 400,
    'order[name]': 'asc',
    ...this.filter,
  }).then((res) => {
    return res.data['hydra:member']
      .sort(() => sortAsc('name'))
      .map((el) => ({ text: el.name, value: el.id, description: el.description }));
  });
}

export function searchConsumableVariations(term) {
  return Dataman.DB[CAT_CONSUMABLE_VARIATIONS].getList({
    name: term,
    properties: ['id', 'name', 'description'],
    itemsPerPage: 400,
    'order[name]': 'asc',
    ...this.filter,
  }).then((res) => {
    return res.data['hydra:member']
      .sort(() => sortAsc('name'))
      .map((el) => ({ text: el.name, value: el.id, description: el.description }));
  });
}

export function loadProductTypes() {
  return Dataman.Filter.getProductTypes({ itemsPerPage: 300 })
    .then((res) => res.data.sort(() => sortAsc('name')))
    .catch(console.error);
}

export function loadProductStatuses() {
  let ordering = (a, b) => {
    if (a.name.toLowerCase() > b.name.toLowerCase()) {
      return 1;
    } else if (a.name.toLowerCase() < b.name.toLowerCase()) {
      return -1;
    } else {
      return 0;
    }
  };
  let iPromise = (resolve, reject) => {
    let iResolvePromise = (r) => {
      resolve(r.data.sort(ordering));
    };
    Dataman.Filter.getProductStatuses().then(iResolvePromise).catch(reject);
  };
  return new Promise(iPromise);
}

export function loadProductFileGroups() {
  const exclude = [6];
  return loadFileGroups(FILE_GROUP_MODULE_PRODUCTS).then((list) => list.filter((el) => !exclude.includes(el.id)));
}

export function loadUSPTypes(filter = {}) {
  return Dataman.DB.types
    .getList({
      properties: ['id', 'name', 'description'],
      itemsPerPage: 400,
      'parent.id': USP_TYPE_ID,
      'order[translations.name]': 'asc',
      ...filter,
    })
    .then((res) => {
      return res.data['hydra:member']
        .sort(() => sortAsc('name'))
        .map((el) => ({ text: el.name, value: el.id, description: el.description }));
    });
}

export async function searchUSPTypes(term, filter = {}) {
  return ItemMaster.getList({
    category: CAT_TYPES,
    filter: { 'translations.name': term, relevantOrder: 'name', 'parent.id': USP_TYPE_ID, ...filter },
  }).then((data) => {
    return data.map((el) => ({ text: el.name, value: el.id }));
  });
}

function mapCompetitor(item) {
  return {
    text: item.name,
    value: item.id,
    description: item.description,
  };
}

async function loadTypesByParentId(parentId, filter = {}) {
  return Dataman.DB.types
    .getList({
      properties: ['id', 'name', 'description'],
      itemsPerPage: 200,
      'parent.id': parentId,
      'order[translations.name]': 'asc',
      ...filter,
    })
    .then((res) => {
      return res.data['hydra:member'].map(mapCompetitor);
    });
}

async function searchTypesByParentId(parentId, term, filter = {}) {
  return ItemMaster.getList({
    category: CAT_TYPES,
    filter: {
      'translations.name': term,
      relevantOrder: 'name',
      'parent.id': parentId,
      ...filter,
    },
  }).then((data) => {
    return data.map((el) => ({
      text: el.name,
      value: el.id,
    }));
  });
}

export async function loadCompetitors(filter = {}) {
  return loadTypesByParentId(COMPETITORS_TYPE_ID, filter);
}

export async function searchCompetitorsTypes(term, filter = {}) {
  return searchTypesByParentId(COMPETITORS_TYPE_ID, term, filter);
}

export async function loadCannaCompetitors(filter = {}) {
  return loadTypesByParentId(CANNA_COMPETITORS_TYPE_ID, filter);
}

export async function searchCannaCompetitors(term, filter = {}) {
  return searchTypesByParentId(CANNA_COMPETITORS_TYPE_ID, term, filter);
}

const EXCLUDED_TYPES = [36, 297, COMPETITORS_TYPE_ID];

export function productTypesLoader(filter = {}) {
  return Dataman.DB.types
    .getList({
      properties: ['id', 'name', 'description'],
      itemsPerPage: 200,
      and_group_or_parent: [[...EXCLUDED_TYPES, 'not']], // Images, USP, Competitors
      'order[translations.name]': 'asc',
      ...filter,
    })
    .then((res) => {
      return res.data['hydra:member']
        .sort(() => sortAsc('name'))
        .map((el) => ({ text: el.name, value: el.id, description: el.description }))
        .filter((item) => ![297, 279, 36].includes(item.value));
    });
}

export async function searchTypes(term, filter = {}) {
  return ItemMaster.getList({
    category: CAT_TYPES,
    filter: {
      'translations.name': term,
      relevantOrder: 'name',
      and_group_or_parent: [[...EXCLUDED_TYPES, 'not']], // USP, Images
      'order[translations.name]': 'asc',
      ...filter,
    },
  }).then((data) => {
    return data.filter((el) => ![297, 279, 36].includes(el.id)).map((el) => ({ text: el.name, value: el.id }));
  });
}

export async function searchAllTypes(term) {
  return ItemMaster.getList({
    category: CAT_TYPES,
    filter: {
      'translations.name': term,
      relevantOrder: 'name',
      ...this.filter,
    },
  }).then((data) => {
    return data.map((el) => ({ text: el.name, value: el.id })).filter(({ value }) => value !== TYPE_ID_IMAGES);
  });
}

export async function searchProduct(term) {
  const filter = { name: term, properties: ['id', 'name', 'category'] };
  return ItemMaster.getList({ filter, category: CAT_PRODUCTS }).then((data) => {
    return data.map((el) => ({ text: el.name, value: el.id }));
  });
}

export function loadProducts() {
  const filter = { itemsPerPage: 100, properties: ['id', 'name', 'category'] };
  return Dataman.DB.getProducts(filter).then((res) => {
    return res.data['hydra:member'].sort(() => sortAsc('name')).map((el) => ({ text: el.name, value: el.id }));
  });
}

export async function economicRegions() {
  return Dataman.Filter.getEconomicRegions({
    params: { itemsPerPage: 300 },
  }).then((res) => {
    return res.data.sort(sortBy('name'));
  });
}

export default {
  loadConsumableVariations,
  searchConsumableVariations,
  loadProductTypes,
  loadProductStatuses,
  loadProductFileGroups,
  loadUSPTypes,
  searchUSPTypes,
  searchProduct,
  loadProducts,
  productTypesLoader,
  searchTypes,
};
